import * as rxjs from 'rxjs'

import type * as ecModels from '../../../../../models'
import * as models from '../../models'
import type { Plugin } from '../../plugin'

/**
 * Emits a {@link models.EditsSelection.Result} by looking up
 * {@link models.EditsSelection} in the given {@link Plugin}
 */
export function findEdits<TTypeParams extends ecModels.TypeParams.Constraint>(
  { current, create, update, delete: _delete }: Readonly<models.EditsSelection>,
  plugin: Plugin<TTypeParams>,
): rxjs.Observable<models.EditsSelection.Result<TTypeParams>> {
  return plugin.feature.state.asObservable.pipe(
    rxjs.switchMap(state => {
      // NOTE: must use `=== true`, since there are other possible truthy values
      // that do not mean select all
      const allCreations = create === true
      const allUpdates = update === true
      const allDeletions = _delete === true

      /**
       * We want {@link models.EditsSelection.Result.deletions} to be readonly but
       * {@link models.EditsSelection.Result.creations} and
       * {@link models.EditsSelection.Result.updates} to be mutable because we are
       * going to add to those below
       */
      type Results<T = models.EditsSelection.Result<TTypeParams>> = {
        [K in keyof T]: K extends 'deletions' ? T[K] : Mutable<T[K]>
      }

      // TODO: find a way to make this DRY
      const results: Results = {
        creations: (allCreations
          ? // If we are getting all creations, we just take the entire lookup
            Object.entries(state.creationsLookup).map(([id, data]) => ({
              data,
              id: id as keyof typeof state.creationsLookup,
            }))
          : // Otherwise, we extract the requested entries
            (create?.map(id => ({
              data: state.creationsLookup[id],
              id,
            })) ?? [])
        ).filter(isValidEntry),
        deletions: allDeletions ? Array.from(state.deletions) : (_delete ?? []),
        updates: (allUpdates
          ? // If we are getting all updates, we just take the entire lookup
            Object.entries(state.updatesLookup).map(([id, data]) => ({
              data,
              id,
            }))
          : // Otherwise, we extract the requested entries
            (update?.map(id => ({
              data: state.updatesLookup[id],
              id,
            })) ?? [])
        ).filter(isValidEntry),
      }

      if (!current) {
        // If we don't need to add the current edit to the results, then we are
        // done
        return rxjs.of(results)
      }

      // Otherwise, we need to get the current entity ID
      return plugin.collection.plugins.currentEntityPlugin.feature.selectors
        .getId({ target: current === true ? undefined : current.target })
        .asObservable.pipe(
          rxjs.map(id => {
            if (!id) {
              // No current id => nothing to add to the results
              return results
            }
            // TODO: find a way to make this DRY
            if (models.CreationId.isCreationId(id)) {
              // If the focused is a creation id...
              if (
                !results.creations.some(
                  ({ id: existingIdentifier }) => id === existingIdentifier,
                )
              ) {
                // And it is not already in the results...
                const data = state.creationsLookup[id]
                if (data) {
                  // And it has data, then we add it to the results
                  results.creations.push({ data, id })
                }
              }
            } else {
              // If the focused is an update id (i.e. not a creation id)...
              if (
                !results.updates.some(
                  ({ id: existingIdentifier }) => id === existingIdentifier,
                )
              ) {
                // And it is not already in the results...
                const data = state.updatesLookup[id]
                if (data) {
                  // And it has data, then we add it to the results
                  results.updates.push({ data, id })
                }
              }
            }

            return results
          }),
        )
    }),
    // NOTE: Using take(1) ensures that we capture the latest state at the
    // moment of submitting the edits. This is crucial because the state may
    // continue to change, and we want to avoid those additional emissions,
    // since that will cause relay to timeout
    rxjs.take(1),
  )
}

/**
 * Used for filtering out entries with missing info
 */
function isValidEntry<
  T extends models.EditsSelection.Result<any>['creations' | 'updates'][number],
>(item: Partial<T>): item is T {
  return !!item.id && !!item.data
}
